import type {
  RichTextEditorProps,
  RichTextEditorRef,
  ToolbarConfig,
} from "./rich-text-editor.type";

import Quill from "quill";
import {
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
  useState,
} from "react";

import { htmlSanitizer } from "./rich-text-editor.helper";

import "quill/dist/quill.snow.css";
import "quill/dist/quill.bubble.css";

import "./rich-text-editor.style.less";

// Create a unique ID for each editor instance to avoid conflicts
let uniqueId = 0;

// Default toolbar configuration
export const DEFAULT_TOOLBAR_CONFIG: ToolbarConfig = [
  [{ header: [1, 2, 3, false] }],
  ["bold", "italic", "underline", "link", { color: ["#000000", "#FF0000"] }],
  [{ list: "ordered" }, { list: "bullet" }],
  ["clean"],
];

/**
 * Safely set HTML content in Quill
 * This uses Quill's clipboard to handle the HTML, which provides some sanitization
 */
const safelySetHtml = (quill: Quill, html: string) => {
  const htmlSanitized = htmlSanitizer(html);

  // Use Quill's clipboard to handle the HTML, which provides some sanitization
  quill.clipboard.dangerouslyPasteHTML(htmlSanitized);
};

/**
 * RichTextEditor Component
 *
 * A React wrapper around Quill.js rich text editor that provides a customizable
 * and controlled rich text editing experience.
 */
export const RichTextEditor = forwardRef<
  RichTextEditorRef,
  RichTextEditorProps
>(
  (
    {
      value,
      onChange,
      readOnly = false,
      className = "",
      theme = "snow",
      label,
      placeholder = "",
      toolbarConfig = DEFAULT_TOOLBAR_CONFIG,
      ...rest
    },
    ref
  ) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const quillRef = useRef<Quill | null>(null);
    const [isQuillReady, setIsQuillReady] = useState(false);
    const idRef = useRef<number>(-1);
    const isCleaningUpRef = useRef(false);

    /**
     * Completely clean up Quill and the DOM
     * This is important to prevent memory leaks and ensure proper re-initialization
     */
    const cleanupQuill = () => {
      if (isCleaningUpRef.current) return;
      isCleaningUpRef.current = true;

      if (quillRef.current) {
        quillRef.current.off("text-change");
        quillRef.current = null;
      }

      if (containerRef.current) {
        containerRef.current.innerHTML = "";
      }

      setIsQuillReady(false);
      isCleaningUpRef.current = false;
    };

    // Expose the Quill instance via ref
    useImperativeHandle(
      ref,
      () => ({
        getEditor: () => quillRef.current,
      }),
      [quillRef.current]
    );

    // Assign a unique ID on first mount and clean up on unmount
    useEffect(() => {
      if (idRef.current === -1) {
        idRef.current = uniqueId++;
      }

      return () => {
        cleanupQuill();
      };
    }, []);

    // Initialize Quill
    useEffect(() => {
      // If we're cleaning up, don't initialize
      if (isCleaningUpRef.current) return;

      // If Quill is already initialized, clean it up first
      if (quillRef.current) {
        cleanupQuill();
      }

      // Check that the container exists
      if (!containerRef.current) return;

      try {
        // Create a simple div for Quill to initialize in
        containerRef.current.innerHTML = "<div></div>";

        // Initialize Quill with toolbar configuration
        const quill = new Quill(
          containerRef.current.firstChild as HTMLElement,
          {
            theme: theme,
            modules: {
              toolbar: toolbarConfig,
            },
            placeholder: placeholder,
            readOnly: readOnly,
          }
        );

        // Initialize with value if it exists
        if (value) {
          safelySetHtml(quill, value);
        }

        // Add event listener for text changes
        quill.on("text-change", () => {
          if (onChange) {
            const html = quill.root.innerHTML;
            onChange(html);
          }
        });

        // Apply the readOnly state
        if (readOnly) {
          quill.disable();
        } else {
          quill.enable();
        }

        quillRef.current = quill;
        setIsQuillReady(true);
      } catch (error) {
        console.error("Error initializing Quill:", error);
      }
    }, [theme, placeholder, toolbarConfig]);

    // Update content when value prop changes
    useEffect(() => {
      if (quillRef.current && value !== undefined) {
        const currentContent = quillRef.current.root.innerHTML;
        if (currentContent !== value) {
          safelySetHtml(quillRef.current, value);
        }
      }
    }, [value]);

    // Update readOnly mode when readOnly prop changes
    useEffect(() => {
      if (!quillRef.current) return;

      if (readOnly) {
        quillRef.current.disable();
      } else {
        quillRef.current.enable();
      }

      // Set data attribute for testing
      if (containerRef.current) {
        containerRef.current.setAttribute("data-readonly", readOnly.toString());
      }
    }, [readOnly]);

    return (
      <div
        className={`quill-editor ${className}`}
        data-testid="rich-text-editor"
        ref={containerRef}
        aria-label={label ?? "Rich text editor"}
        role="textbox"
        data-quill-ready={isQuillReady.toString()}
        data-theme={theme}
        data-placeholder={placeholder}
        data-readonly={readOnly.toString()}
        {...rest}
      />
    );
  }
);

RichTextEditor.displayName = "RichTextEditor";
